import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { AnnieSessionSummary } from "@/hooks/useAnnie";
import { ChatSessionRail } from "@/ui/Chat/ChatSessionRail";

type Props = {
	sessions: AnnieSessionSummary[] | undefined;
	isLoading: boolean;
	isError: boolean;
	activeSessionId: string;
	deletingSessionId: string | null;
	onNewChat: () => void;
	onSelect: (sessionId: string) => void;
	onDelete: (sessionId: string) => void;
};

/**
 * The Annie chat window's collapsible session rail: maps Annie session summaries
 * onto the generic `ChatSessionRail` and supplies the translated copy + Annie's
 * exact test ids.
 */
export const AnnieSessionsSidebar: FC<Props> = ({ sessions, isLoading, isError, activeSessionId, deletingSessionId, onNewChat, onSelect, onDelete }) => {
	const { t } = useTranslation();

	return (
		<ChatSessionRail
			dataTestId="annie-sessions-sidebar"
			newButtonDataTestId="annie-sessions-new-chat"
			items={sessions?.map((session) => ({ id: session.session_id, title: session.title }))}
			activeId={activeSessionId}
			deletingId={deletingSessionId}
			isLoading={isLoading}
			isError={isError}
			onNew={onNewChat}
			onSelect={onSelect}
			onDelete={onDelete}
			newLabel={t("annie.sessions.new")}
			emptyLabel={t("annie.sessions.empty")}
			errorLabel={t("annie.sessions.failed")}
			deleteAriaLabel={t("annie.sessions.delete")}
			getItemDataTestId={(id) => `annie-session-item-${id}`}
			getDeleteDataTestId={(id) => `annie-session-delete-${id}`}
		/>
	);
};
